import { useEffect, useState } from 'react';

import { useSearchParams, useNavigate } from 'react-router-dom';

import Card from '#globalComponents/Card/Card';

/**
 * Handles the redirect back from the Aircall OAuth flow.
 * The backend sets ?success=true or ?error=<code> on the redirect URL.
 * On success we navigate to CRM settings; on error we show a message first.
 */
const AircallOAuthCallbackPage = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [status, setStatus] = useState('processing');
	const [errorMessage, setErrorMessage] = useState('');

	useEffect(() => {
		const success = searchParams.get('success');
		const error = searchParams.get('error');

		if (success === 'true') {
			setStatus('success');
			// Brief flash of success, then redirect to integrations settings
			setTimeout(() => navigate('/crm/settings', { replace: true }), 1500);
		} else if (error) {
			setStatus('error');
			const messages = {
				invalid_state: 'Die Sitzung ist abgelaufen. Bitte versuchen Sie es erneut.',
				missing_parameters: 'Fehlende Parameter in der Antwort von Aircall.',
				oauth_failed: 'Die Autorisierung bei Aircall ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
			};
			setErrorMessage(messages[error] || `Fehler bei der Autorisierung: ${error}`);
		} else {
			// No clear params — redirect to settings
			navigate('/crm/settings', { replace: true });
		}
	}, [searchParams, navigate]);

	return (
		<div className="min-h-screen flex items-center justify-center bg-bgBase-300">
			<Card className="max-w-md w-full p-8">
				<div className="text-center">
					{status === 'processing' && (
						<>
							<div className="mx-auto h-16 w-16 mb-4">
								<div className="animate-spin h-16 w-16 border-4 border-primary-500 border-t-transparent rounded-full" />
							</div>
							<h2 className="text-lg font-semibold text-text mb-2">Verarbeite Autorisierung</h2>
							<p className="text-sm text-text">Aircall wird verbunden…</p>
						</>
					)}

					{status === 'success' && (
						<>
							<div className="mx-auto h-16 w-16 mb-4 flex items-center justify-center">
								<svg
									className="h-16 w-16 text-success-500"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
							</div>
							<h2 className="text-lg font-semibold text-text mb-2">Aircall verbunden!</h2>
							<p className="text-sm text-text">Sie werden weitergeleitet…</p>
						</>
					)}

					{status === 'error' && (
						<>
							<div className="mx-auto h-16 w-16 mb-4 flex items-center justify-center">
								<svg
									className="h-16 w-16 text-warning-500"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
							</div>
							<h2 className="text-lg font-semibold text-text mb-2">Verbindung fehlgeschlagen</h2>
							<p className="text-sm text-text mb-4">{errorMessage}</p>
							<button
								onClick={() => navigate('/crm/settings', { replace: true })}
								className="text-sm text-primary-600 hover:text-primary-700 font-medium"
							>
								Zurück zu den Einstellungen
							</button>
						</>
					)}
				</div>
			</Card>
		</div>
	);
};

export default AircallOAuthCallbackPage;
